import React from 'react';

import { BlankPassingRateHeader } from './blankPassingRateHeader';
import { BlankFilterSection, FilterState } from './blankFilterSection';

import { Spinner, responsesWithStatus, hashToCollection, ResponsePageNumbers, ResponseList, } from '../../../Shared/index';
import { filterBySuboptimalBlank, filterByBlankCues } from '../../libs/filterResponsesByBlank';
import { boldCuesInTextHTML } from '../../libs/boldCuesInText';
import { requestGet, requestPost } from '../../../../modules/request/index';
import { unmatchResponses } from '../../actions/responses';

const DEFAULT_FILTERS = {
  suboptimalBlankIndices: [],
  searchValues: []
}

interface Response {
  author?: string;
  count?: number;
  feedback?: string;
  first_attempt_count?: number;
  id: string;
  key: string;
  optimal?: boolean;
  text: string;
  [key: string]: any;
}

interface Question {
  blankByBlankGrading?: any;
  caseInsensitive?: boolean;
  conceptID?: string;
  prompt: string;
  [key: string]: any;
}

interface FillInBlankResponsesManagerProps {
  admin?: boolean;
  concepts?: any;
  conceptsFeedback?: any;
  Constants: any;
  deleteResponse: Function;
  dispatch: Function;
  filterActions: any;
  filters?: any;
  getGradedResponsesWithCallback: Function;
  massEdit?: any;
  massEditActions: any;
  mode?: string;
  question: Question;
  questionActions: any;
  questionID: string;
  rematchOne?: Function;
  states?: any;
  storeResponsesLocally?: boolean;
  submitResponseEdit: Function;
}
export const FillInBlankResponsesManager: React.FC<FillInBlankResponsesManagerProps> = ({
  admin,
  concepts,
  conceptsFeedback,
  Constants,
  deleteResponse,
  dispatch,
  filterActions,
  filters,
  getGradedResponsesWithCallback,
  massEdit,
  massEditActions,
  mode,
  question,
  questionActions,
  questionID,
  rematchOne,
  states,
  submitResponseEdit
}) => {
  const PAGE_SIZE = 20;
  const FETCH_LIMIT = 1000;

  const [isLoadingResponses, setIsLoadingResponses] = React.useState(true);
  const [allResponses, setAllResponses] = React.useState<Response[]>([]);
  const [filteredResponses, setFilteredResponses] = React.useState<Response[]>([]);

  const [currentFilters, setCurrentFilters] = React.useState<FilterState>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = React.useState(1);
  
  const [health, setHealth] = React.useState(null)

  React.useEffect(() => {
    setCurrentFilters(DEFAULT_FILTERS)
  }, [questionID])

  React.useEffect(() => {
    fetchResponses();
    fetchPromptHealth()
  }, [questionID]);

  React.useEffect(() => {
    applyFilters();
  }, [currentFilters, allResponses]);

  const fetchResponses = () => {
    setIsLoadingResponses(true);

    requestPost(
      `${window.postbuildEnv.QUILL_CMS}/questions/${questionID}/responses/search`,
      {
        search: {
          filters: {
            author: [],
            status: []
          },
          sort: {
            column: 'count',
            direction: 'desc'
          },
          excludeMisspellings: false,
          text: '',
          pageNumber: 1,
          perPage: FETCH_LIMIT
        }
      },
      (data) => {
        const responses = data?.results || [];

        const responsesArray = responses.map((resp: any) => ({
          ...resp,
          key: resp.id
        }));

        setAllResponses(responsesArray);
        setIsLoadingResponses(false);
      },
      (error) => {
        setIsLoadingResponses(false);
      }
    );
  };

  const fetchPromptHealth = () => {
    requestGet(`${window.postbuildEnv.QUILL_CMS}/questions/${questionID}/health`, (body) => {
      setHealth(body)
    });
  }

  const applyFilters = () => {
    let filtered = [...allResponses];

    // Apply suboptimal blank filters (AND logic - show responses suboptimal in ALL selected blanks)
    if (currentFilters.suboptimalBlankIndices && currentFilters.suboptimalBlankIndices.length > 0) {
      // For each selected blank, progressively filter down to responses suboptimal in that blank
      currentFilters.suboptimalBlankIndices.forEach(blankIndex => {
        filtered = filterBySuboptimalBlank(
          filtered,
          question,
          blankIndex
        );
      });
    }

    // Apply search values filter
    if (currentFilters.searchValues && currentFilters.searchValues.length > 0) {
      const hasNonEmptySearch = currentFilters.searchValues.some(v => v && v.trim());
      if (hasNonEmptySearch) {
        filtered = filterByBlankCues(filtered, question, currentFilters.searchValues);
      }
    }

    setFilteredResponses(filtered);
    setCurrentPage(1); // Reset to page 1 when filters change
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setCurrentFilters(newFilters);
  };

  const expand = (responseKey: string) => {
    dispatch(filterActions.toggleExpandSingleResponse(responseKey));
  };

  const updateParentResponse = () => {
    // Refresh responses after update
    fetchResponses();
  };

  // Store original text for each response (needed for rematch/grading)
  const originalTexts = React.useMemo(() => {
    const map = new Map<string, string>();
    filteredResponses.forEach(resp => {
      map.set(resp.id, resp.text);
    });
    return map;
  }, [filteredResponses]);

  // Preprocess responses to bold cues for display only
  const preprocessResponsesForDisplay = (responses: Response[]): Response[] => {
    return responses.map(response => ({
      ...response,
      displayText: boldCuesInTextHTML(response.text, question.prompt)
    }));
  };

  if (isLoadingResponses) {
    return (
      <div className="fill-in-blank-responses-manager">
        <Spinner />
      </div>
    );
  }

  // Calculate pagination
  const totalPages = Math.ceil(filteredResponses.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const responsesForCurrentPage = filteredResponses.slice(startIndex, endIndex);

  // Preprocess responses with bolded cues for display (only for current page)
  const preprocessedResponses = preprocessResponsesForDisplay(responsesForCurrentPage);

  // Add status code to responses for display
  const responsesForDisplay = responsesWithStatus(
    preprocessedResponses.reduce((acc, resp) => {
      acc[resp.id] = resp;
      return acc;
    }, {} as any)
  );

  // getResponse function that returns original text for grading operations
  const getResponse = (responseID: string) => {
    const displayResponse = preprocessedResponses.find(r => r.id === responseID);
    if (!displayResponse) return undefined;

    // Return response with original text for grading operations (rematch, etc.)
    return {
      ...displayResponse,
      text: originalTexts.get(responseID) || displayResponse.originalText || displayResponse.text
    };
  };

  const renderPageNumbers = () => {
    return (
      <ResponsePageNumbers
        numberOfPages={totalPages}
        responsePageNumber={currentPage}
        updatePageNumber={updatePageNumber}
      />
    )
  }

  const updatePageNumber = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const renderDisplayingMessage = () => {
    const total = filteredResponses.length;
    const start = total === 0 ? 0 : startIndex + 1;
    const end = Math.min(endIndex, total);
    const message = `Displaying ${start}-${end} of ${total} responses`;
    return <p className="label">{message}</p>;
  };

  const areAllResponsesExpanded = () => {
    const responsesArray = hashToCollection(responsesForDisplay);
    if (responsesArray.length === 0) return false;
    return responsesArray.every(r => filters?.expanded?.[r.key]);
  };

  const toggleAllResponses = () => {
    const newExpandedState = { ...(filters?.expanded || {}) };
    const responsesArray = hashToCollection(responsesForDisplay);
    const shouldExpand = !areAllResponsesExpanded();

    responsesArray.forEach(r => {
      newExpandedState[r.key] = shouldExpand;
    });
    dispatch(filterActions.expandAllResponses(newExpandedState));
  };

  const renderExpandAllResponsesButton = () => {
    const allExpanded = areAllResponsesExpanded();
    const buttonText = allExpanded ? 'Hide All Responses' : 'Expand All Responses';

    return (
      <button className="quill-button contained medium green focus-on-light" onClick={toggleAllResponses} type="button">
        {buttonText}
      </button>
    );
  };

  const handleUnmatchSelected = () => {
    const selectedIds = massEdit?.selectedResponses || [];
    if (selectedIds.length === 0) {
      alert('Please select responses to unmatch');
      return;
    }

    if (window.confirm(`Remove manual grading for ${selectedIds.length} selected responses?`)) {
      dispatch(unmatchResponses(selectedIds, questionID));
      // Refresh responses after a short delay to allow server to process
      selectedIds.map(selectedId => {
        const response = allResponses.find(resp => resp.id === selectedId)
        rematchOne(response, null, question, questionID, null, null)
      })
      setTimeout(() => {
        fetchResponses();
        // Clear selected responses after unmatch
        dispatch(massEditActions.clearResponsesFromMassEditArray());
      }, 1000);
    }
  };

  const renderUnmatchSelectedButton = () => {
    const selectedCount = massEdit?.selectedResponses?.length || 0;

    return (
      <button
        className="quill-button contained medium green focus-on-light"
        disabled={selectedCount === 0}
        onClick={handleUnmatchSelected}
        type="button"
      >
        Remove Manual Grading
      </button>
    );
  };

  return (
    <div className="fill-in-blank-responses-manager">
      {health && (
        <div className="attempts-and-response-count-header">
          <span>Total Attempts: {health.total_number_of_attempts}</span>
          <span> | </span>
          <span>Total Unique Responses: {health.total_number_of_responses}</span>
        </div>
      )}

      {question.blankByBlankGrading && (
        <BlankPassingRateHeader
          question={question}
          responses={allResponses}
        />
      )}

      <BlankFilterSection
        currentFilters={currentFilters}
        onFilterChange={handleFilterChange}
        question={question}
        responses={allResponses}
      />

      <div className="multi-response-actions">
        {renderExpandAllResponsesButton()}
        {renderUnmatchSelectedButton()}
      </div>

      {renderDisplayingMessage()}

      {renderPageNumbers()}

      <ResponseList
        addResponsesToMassEditArray={massEditActions.addResponsesToMassEditArray}
        admin={admin || false}
        ascending={filters?.ascending}
        conceptID={question.conceptID}
        concepts={concepts}
        conceptsFeedback={conceptsFeedback}
        Constants={Constants}
        deleteResponse={deleteResponse}
        dispatch={dispatch}
        expand={expand}
        expanded={filters?.expanded || {}}
        fillInTheBlank={true}
        getGradedResponsesWithCallback={getGradedResponsesWithCallback}
        getResponse={getResponse}
        massEdit={massEdit || { selectedResponses: [] }}
        massEditActions={massEditActions}
        mode={mode || 'questions'}
        question={question}
        questionActions={questionActions}
        questionID={questionID}
        rematchOne={rematchOne}
        removeResponsesFromMassEditArray={massEditActions.removeResponsesFromMassEditArray}
        responses={hashToCollection(responsesForDisplay).sort((resp1, resp2) => resp2.count - resp1.count)}
        selectedFocusPoints={undefined}
        selectedIncorrectSequences={undefined}
        states={states}
        submitResponseEdit={submitResponseEdit}
        updateParentResponse={updateParentResponse}
      />

      {renderPageNumbers()}
    </div>
  );
};
